// src/context/AppContext.jsx - FIXED
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import StorageService from '../services/StorageService';
import AuthService from '../services/AuthService';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  const db = getFirestore(); // Initialize Firestore instance

  // Helper: Find Kitchen Staff in ANY restaurant
  const findKitchenStaff = async (email) => {
    try {
      console.log('👨‍🍳 Searching for kitchen staff account:', email);
      // Use Collection Group Query to search 'kitchen_staff' across all restaurants
      const staffQuery = query(collectionGroup(db, 'kitchen_staff'), where('email', '==', email));
      const snapshot = await getDocs(staffQuery);

      if (!snapshot.empty) {
        const staffDoc = snapshot.docs[0];
        const staffData = staffDoc.data();
        // The parent of a subcollection item is the subcollection, the parent of that is the Restaurant Doc
        // path: restaurants/{restaurantId}/kitchen_staff/{staffId}
        const restaurantId = staffDoc.ref.parent.parent.id;
        
        console.log('✅ Found kitchen staff account:', {
            id: staffDoc.id,
            restaurantId: restaurantId,
            name: staffData.name
        });

        return {
          ...staffData,
          id: staffDoc.id,
          restaurantId: restaurantId, // Append the restaurant ID to the user object
          role: 'kitchen'
        };
      }
      return null;
    } catch (error) {
      console.error('❌ Error searching kitchen staff:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('🚀 Initializing app...');
        await new Promise(resolve => setTimeout(resolve, 1500));
        await StorageService.init();
        setInitialized(true);
        console.log('✅ App initialized successfully');
      } catch (error) {
        console.error('❌ App initialization error:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Listen to Firebase Auth state changes
  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged(async (firebaseUser) => {
      console.log('🔄 Auth state changed:', firebaseUser?.email);
      
      if (firebaseUser) {
        setAuthUser(firebaseUser);
        
        try {
          // 1. Load General Data (Owners/Admins)
          const data = await StorageService.getData();
          
          // 2. Check Superadmin
          if (data.superadmin && firebaseUser.email === data.superadmin.email) {
            console.log('✅ Superadmin authenticated');
            setUser(data.superadmin);
            setUserType('superadmin');
            return;
          } 

          // 3. Check Restaurant Owner
          const restaurant = data.restaurants?.find(r => r.email === firebaseUser.email);
          if (restaurant) {
             if (restaurant.status !== 'Active') {
                console.warn('⚠️ Restaurant is inactive');
                await AuthService.signOut();
                return;
             }
             console.log('✅ Restaurant owner authenticated');
             setUser(restaurant);
             setUserType('owner');
             return;
          }

          // 4. ✅ NEW: Check Kitchen Staff (Fallback if not Owner)
          const kitchenStaff = await findKitchenStaff(firebaseUser.email);
          if (kitchenStaff) {
             console.log('✅ Kitchen Staff authenticated via Persistence');
             setUser(kitchenStaff);
             setUserType('kitchen');
             return;
          }

          console.warn('⚠️ User not found in any role:', firebaseUser.email);
          // Optional: Don't sign out immediately, allows debugging
          // await AuthService.signOut(); 

        } catch (error) {
          console.error('❌ Error loading user data:', error);
        }
      } else {
        console.log('🔓 User signed out');
        setAuthUser(null);
        setUser(null);
        setUserType(null);
      }
    });

    return () => unsubscribe();
  }, [db]); // added db dependency

  const login = async (email, password) => {
    console.log('🔐 Login function called:', { email });

    try {
      // 1. Firebase Auth
      const authResult = await AuthService.signIn(email, password);
      
      if (!authResult.success) {
        return { success: false, message: authResult.message, needsVerification: authResult.needsVerification };
      }

      StorageService.clearCache();
      const data = await StorageService.getData();
      
      // 2. Check Superadmin
      if (data.superadmin && email === data.superadmin.email) {
        setUser(data.superadmin);
        setUserType('superadmin');
        return { success: true, type: 'superadmin' };
      }

      // 3. Check Restaurant Owner
      const restaurant = data.restaurants?.find(r => r.email === email);
      if (restaurant) {
        if (restaurant.status !== 'Active') {
          await AuthService.signOut();
          return { success: false, message: 'Restaurant is inactive.' };
        }
        setUser(restaurant);
        setUserType('owner');
        return { success: true, type: 'owner', restaurantId: restaurant.id };
      }

      // 4. ✅ NEW: Check Kitchen Staff
      const kitchenStaff = await findKitchenStaff(email);
      if (kitchenStaff) {
         if (kitchenStaff.status === 'Inactive') {
             await AuthService.signOut();
             return { success: false, message: 'Staff account is inactive.' };
         }
         setUser(kitchenStaff);
         setUserType('kitchen');
         // Important: Return the restaurant ID found in the staff search
         return { success: true, type: 'kitchen', restaurantId: kitchenStaff.restaurantId };
      }

      console.warn('❌ User not found in database');
      await AuthService.signOut();
      return { success: false, message: 'User account not found. Please contact support.' };

    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const logout = async () => {
    await AuthService.signOut();
    setUser(null);
    setUserType(null);
    setAuthUser(null);
  };

  const sendPasswordReset = async (email) => {
    return await AuthService.sendPasswordResetEmail(email);
  };

  const resendVerification = async () => {
    return await AuthService.resendVerificationEmail();
  };

  if (loading && !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-orange-500 mb-4"></div>
          <p className="text-xl text-gray-700 font-semibold">Welcome back! 🍽️</p>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{
      user,
      userType,
      authUser,
      login,
      logout,
      sendPasswordReset,
      resendVerification,
      loading: false,
      setUser,
      setUserType,
      initialized
    }}>
      {children}
    </AppContext.Provider>
  );
};